import { Edit, Save } from '@mui/icons-material';
import {
  Typography,
  Card,
  IconButton,
  TextField,
  Box,
  Tooltip,
} from '@mui/material';
import { FormTemplateType } from 'api/models';
import { useSaveFormTemplate } from 'hooks/useSaveFormTemplate';
import { mutation } from 'operations';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { formTemplateState } from 'state';
import { GeneralIds, ModelTypes } from 'types/types';

interface FormEditorCardProps {
  formTemplateType?: FormTemplateType;
  isViewOnly?: boolean;
}

export function FormEditorCard({
  formTemplateType,
  isViewOnly = false,
}: FormEditorCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [formName, setFormName] = useState(formTemplateType?.name);
  const [formTemplate, setFormTemplate] = useRecoilState(formTemplateState);
  const { projectId } = useParams<GeneralIds>();
  const { t } = useTranslation();
  const { save } = useSaveFormTemplate();

  useEffect(() => {
    setFormName(formTemplateType?.name);
  }, [formTemplateType?.name]);

  const onEdit = () => {
    if (isEditing) {
      mutation<FormTemplateType>({
        type: ModelTypes.formTemplateType,
        body: {
          id: formTemplateType?.id ?? '',
          name: formName ?? '',
        },
        ids: {
          id: formTemplateType?.id,
          projectId,
        },
      });
      save();
    }
    setIsEditing(!isEditing);
  };

  return (
    <Card
      elevation={1}
      sx={{
        mt: 1,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        border: theme => `1px solid ${theme.palette.divider}`,
        p: 1,
      }}
    >
      {isEditing ? (
        <Box display={'flex'} flexDirection={'row'} width={'100%'}>
          <TextField
            label={t('forms.formTemplateTypeName')}
            type="text"
            fullWidth
            value={formName}
            onChange={e => setFormName(e.target.value)}
            sx={{ mr: 1 }}
          />

          <TextField
            label={t('dialogs.addField.externalId')}
            type="text"
            fullWidth
            value={formTemplate?.mappings?.externalCommunication?.hl7Identifier}
            onChange={e =>
              // @ts-ignore
              setFormTemplate({
                ...formTemplate,
                mappings: {
                  externalCommunication: {
                    hl7Identifier: e.target.value ?? '',
                  },
                },
              })
            }
          />
        </Box>
      ) : (
        <Typography variant="h6" fontWeight={'bold'} m={1}>
          {formName}
        </Typography>
      )}

      {isViewOnly ? null : (
        <IconButton size="small" onClick={onEdit} sx={{ ml: 1 }}>
          {isEditing ? (
            <Tooltip title={t('general.save')}>
              <Save fontSize="small" />
            </Tooltip>
          ) : (
            <Tooltip title={t('general.edit')}>
              <Edit fontSize="small" />
            </Tooltip>
          )}
        </IconButton>
      )}
    </Card>
  );
}
